import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraMasterDetail } from '@mono/ui-components/admirals';
import { SpecraActionList } from '@mono/ui-components/captains';
import { SpecraBadge, SpecraButton, SpecraSpinner, SpecraText } from '@mono/ui-components/ensigns';
import { SpecraEmptyState, SpecraInlineBanner, SpecraPageHeader, SpecraSegmentedControl } from '@mono/ui-components/lieutenants';
import { Testids } from '@mono/ui-selectors';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ReviewGroup } from '../../../../../../gen/ts/mono/datagateway/v1/registry_records_pb';
import { useAuth } from '../../../contexts/authContext';
import { ErrorState } from '../../Delivery/_shared/components/PageStates';
import { registryKeys } from '../queryKeys';
import { RegistryRecordFrame } from '../RegistryRecordFrame';
import { registryRecordClient } from '../records/registryRecordClient';
import { ReviewDetail } from './ReviewDetail';
// Chip order is the reviewer-facing order; slugs are the `?group=` URL values.
export const REVIEW_GROUPS = [
    {
        group: ReviewGroup.LABOR_CATEGORIES,
        slug: 'labor-categories',
        label: 'Labor categories'
    },
    {
        group: ReviewGroup.MAPPINGS,
        slug: 'mappings',
        label: 'Mappings'
    },
    {
        group: ReviewGroup.PERIODS,
        slug: 'periods',
        label: 'Periods'
    },
    {
        group: ReviewGroup.BURDEN_BASIS,
        slug: 'burden-basis',
        label: 'Burden basis'
    },
    {
        group: ReviewGroup.PEOPLE,
        slug: 'people',
        label: 'People'
    },
    {
        group: ReviewGroup.ENTITIES,
        slug: 'entities',
        label: 'Entities'
    }
];
// Legacy `?kind=` links resolve to the group chip that now holds that kind.
const KIND_GROUP = {
    LCAT_CANDIDATE: ReviewGroup.LABOR_CATEGORIES,
    MAPPING: ReviewGroup.MAPPINGS,
    PERIOD_COERCION: ReviewGroup.PERIODS,
    BURDEN_BASIS: ReviewGroup.BURDEN_BASIS,
    ENTITY_MATCH: ReviewGroup.ENTITIES,
    EMPLOYMENT_CHANGE: ReviewGroup.PEOPLE,
    PERSON_MERGE: ReviewGroup.PEOPLE
};
const KIND_LABEL = {
    LCAT_CANDIDATE: 'Labor category',
    MAPPING: 'Mapping',
    PERIOD_COERCION: 'Period',
    BURDEN_BASIS: 'Burden basis',
    ENTITY_MATCH: 'Entity match',
    EMPLOYMENT_CHANGE: 'Job change',
    PERSON_MERGE: 'Person merge'
};
const groupOf = (item)=>item.group || KIND_GROUP[item.kind] || ReviewGroup.UNSPECIFIED;
const eyebrowOf = (item)=>item.kind === 'ENTITY_MATCH' && groupOf(item) === ReviewGroup.PEOPLE ? 'Possible duplicate person' : KIND_LABEL[item.kind] ?? item.kind;
export const itemKey = (item)=>`${item.kind}:${item.id}`;
const supported = (kind)=>kind in KIND_GROUP;
export function matchesReviewFilters(item, anchor, vehicle) {
    const refs = [
        item.left?.ref,
        item.right?.ref,
        ...item.alternatives.map((s)=>s.ref)
    ];
    return (!anchor || refs.some((r)=>r?.kind === 'LCAT_ANCHOR' && r.id === anchor)) && (!vehicle || refs.some((r)=>r?.kind === 'VEHICLE' && r.id === vehicle));
}
export function RegistryReviewQueuePage() {
    const { user } = useAuth();
    const viewer = `${user?.tenant_id ?? ''}:${user?.id ?? ''}`;
    const qc = useQueryClient();
    const [params, setParams] = useSearchParams();
    const rawGroup = params.get('group') ?? '';
    const rawKind = params.get('kind') ?? '';
    const slugGroup = REVIEW_GROUPS.find((g)=>g.slug === rawGroup);
    const group = rawGroup ? slugGroup?.group ?? ReviewGroup.UNSPECIFIED : supported(rawKind) ? KIND_GROUP[rawKind] : ReviewGroup.UNSPECIFIED;
    const unavailable = rawGroup ? !slugGroup : !!rawKind && !supported(rawKind);
    const anchor = params.get('anchor') ?? '';
    const vehicle = params.get('vehicle') ?? '';
    const selectedKey = params.get('item') ?? '';
    const [selectedKind, selectedId] = selectedKey.split(':');
    const queue = useInfiniteQuery({
        queryKey: [
            ...registryKeys.all,
            'review-queue',
            viewer,
            group
        ],
        initialPageParam: '',
        queryFn: ({ pageParam })=>registryRecordClient.listRegistryReviewItems({
                groups: group ? [
                    group
                ] : [],
                afterCursor: pageParam,
                limit: 50
            }),
        getNextPageParam: (last)=>last.nextCursor || undefined,
        retry: false
    });
    const items = (queue.data?.pages.flatMap((p)=>p.items) ?? []).filter((it)=>supported(it.kind) && matchesReviewFilters(it, anchor, vehicle));
    const selected = useQuery({
        queryKey: [
            ...registryKeys.all,
            'review-item',
            viewer,
            selectedKey
        ],
        queryFn: ()=>registryRecordClient.getRegistryReviewItem({
                kind: selectedKind,
                id: selectedId
            }),
        enabled: !!selectedId && supported(selectedKind),
        retry: false
    });
    const selectedItem = selected.data?.item;
    const item = selectedKey ? selectedItem && matchesReviewFilters(selectedItem, anchor, vehicle) && (!group || group === groupOf(selectedItem)) ? selectedItem : undefined : items[0];
    const pick = (key)=>{
        setParams((prev)=>{
            const next = new URLSearchParams(prev);
            key ? next.set('item', key) : next.delete('item');
            return next;
        });
    };
    const onRecorded = async (keepItem = false)=>{
        toast.success('Decision recorded');
        if (!keepItem && item) {
            const i = items.findIndex((it)=>itemKey(it) === itemKey(item));
            const next = items[i + 1] ?? items.find((it)=>itemKey(it) !== itemKey(item));
            pick(next ? itemKey(next) : '');
        }
        await Promise.all([
            qc.invalidateQueries({
                queryKey: registryKeys.all
            }),
            ...[
                'labor category candidates',
                'labor category',
                'lcat mappings',
                'rate observations',
                'pricing sheet review',
                'registry-records',
                'contact',
                'company profile',
                'contract vehicle',
                'government organization'
            ].map((key)=>qc.invalidateQueries({
                    queryKey: [
                        key
                    ]
                }))
        ]);
    };
    const groupCounts = queue.data?.pages[0]?.groupCounts ?? [];
    const countOf = (g)=>groupCounts.find((c)=>c.group === g)?.count ?? 0;
    const missing = queue.error instanceof ConnectError && queue.error.code === Code.NotFound;
    return /*#__PURE__*/ _jsx(RegistryRecordFrame, {
        // Breadcrumb: Registries / Labor Categories / Review queue (the queue lives under that tab).
        kindLabel: "Labor Categories",
        tab: "lcats",
        title: "Review queue",
        status: queue.isPending ? 'loading' : missing ? 'unavailable' : queue.isError && !queue.data ? 'error' : 'ready',
        error: queue.error,
        onRetry: ()=>void queue.refetch(),
        header: /*#__PURE__*/ _jsx(SpecraPageHeader, {
            titleSize: "home",
            eyebrow: "Review queue",
            title: "Machines suggest, people decide",
            description: "Extraction, price-list parsing and the people graph propose changes with their evidence. A decision is recorded once, with who made it, and every page that uses the row benefits from then on."
        }),
        children: /*#__PURE__*/ _jsxs("div", {
            "data-testid": Testids.registryReview.page,
            className: "min-h-[var(--component-master-detail-min-height)]",
            children: [
                unavailable && /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                    tone: "warning",
                    children: "That group is unavailable. Showing supported review items."
                }),
                /*#__PURE__*/ _jsx(SpecraMasterDetail, {
                    toolbar: {
                        search: false,
                        filters: /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                            label: "Group",
                            testId: Testids.registryReview.kindFilter,
                            value: REVIEW_GROUPS.find((g)=>g.group === group)?.slug ?? '',
                            options: [
                                {
                                    value: '',
                                    label: 'All',
                                    count: REVIEW_GROUPS.reduce((n, g)=>n + countOf(g.group), 0)
                                },
                                ...REVIEW_GROUPS.map((g)=>({
                                        value: g.slug,
                                        label: g.label,
                                        count: countOf(g.group)
                                    }))
                            ],
                            onValueChange: (value)=>setParams((prev)=>{
                                    const next = new URLSearchParams(prev);
                                    next.delete('kind');
                                    value ? next.set('group', value) : next.delete('group');
                                    next.delete('item');
                                    return next;
                                })
                        })
                    },
                    master: /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsx(SpecraActionList, {
                                trailing: null,
                                getItemTestId: ()=>Testids.registryReview.item,
                                items: items.map((it)=>({
                                        id: itemKey(it),
                                        label: it.title,
                                        description: it.subtitle,
                                        eyebrow: /*#__PURE__*/ _jsxs(_Fragment, {
                                            children: [
                                                /*#__PURE__*/ _jsx(SpecraBadge, {
                                                    children: eyebrowOf(it)
                                                }),
                                                " ",
                                                it.sourceLabel
                                            ]
                                        }),
                                        selected: itemKey(it) === (item && itemKey(item)),
                                        onClick: ()=>pick(itemKey(it))
                                    }))
                            }),
                            items.length === 0 && /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                "data-testid": Testids.registryReview.empty,
                                title: "Nothing to review.",
                                description: queue.hasNextPage ? 'No matching items on these pages. Load more to continue.' : 'New suggestions will appear here when they need your decision.'
                            }),
                            queue.isError && /*#__PURE__*/ _jsx(ErrorState, {
                                error: queue.error,
                                onRetry: ()=>void queue.refetch()
                            }),
                            queue.hasNextPage && /*#__PURE__*/ _jsx(SpecraButton, {
                                variant: "ghost",
                                "data-testid": Testids.registryReview.loadMore,
                                disabled: queue.isFetchingNextPage,
                                onClick: ()=>void queue.fetchNextPage(),
                                children: queue.isFetchingNextPage ? 'Loading…' : 'Load more'
                            })
                        ]
                    }),
                    detail: selectedKey && selected.isFetching ? /*#__PURE__*/ _jsx(SpecraSpinner, {
                        label: "Loading review item"
                    }) : selected.error instanceof ConnectError && selected.error.code === Code.NotFound ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                        title: "This item is no longer available",
                        description: "It may have been decided or removed. Choose another suggestion to continue.",
                        action: /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    variant: "secondary",
                                    onClick: ()=>void selected.refetch(),
                                    children: "Retry"
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    variant: "ghost",
                                    onClick: ()=>pick(''),
                                    children: "Choose another suggestion"
                                })
                            ]
                        })
                    }) : selected.isError ? /*#__PURE__*/ _jsx(ErrorState, {
                        title: selected.error instanceof ConnectError && selected.error.code === Code.NotFound ? 'This item is no longer available' : 'This item could not be loaded',
                        error: selected.error,
                        onRetry: ()=>void selected.refetch()
                    }) : item ? /*#__PURE__*/ _jsx(ReviewDetail, {
                        item: item,
                        onRecorded: onRecorded
                    }, itemKey(item)) : selectedKey ? /*#__PURE__*/ _jsx(SpecraText, {
                        children: "This item is no longer in this view. Select another suggestion."
                    }) : null
                })
            ]
        })
    });
}
